import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Button } from "@/components/flexkit/Button";
import { Banner } from "@/components/flexkit/Banner";
import { Link } from "@/components/flexkit/Link";
import { useListGroups } from "@/queries/group-queries";
import { useOrganization } from "@/queries/organization-queries";
import { GroupsList } from "@/components/settings/organization/groups/GroupsList";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { FC } from "react";

type GroupsTabProps = {
    onCreateGroup?: () => void;
    canCreateGroup?: boolean;
};

export const GroupsTab: FC<GroupsTabProps> = ({ onCreateGroup, canCreateGroup = true }) => {
    const { data: groupsData, isLoading, error } = useListGroups();
    const { data: organization, isPending: isLoadingOrganization } = useOrganization();
    const groups = groupsData?.groups || [];

    if (isLoading || isLoadingOrganization) {
        return <LoadingState />;
    }

    const isEnterpriseTier = organization?.tier === OrganizationTier.ENTERPRISE;

    if (error) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-content-secondary">Failed to load groups</p>
            </div>
        );
    }

    if (!isEnterpriseTier) {
        return (
            <Banner
                variant="info"
                className="py-4"
                data-testid="enterprise-tier-banner"
                text={
                    <>
                        Upgrade to{" "}
                        <Link
                            to="/settings/manage-organization"
                            className="font-medium text-content-brand hover:underline"
                        >
                            Enterprise tier
                        </Link>{" "}
                        to manage groups and unlock more features.
                    </>
                }
            />
        );
    }

    if (groups.length === 0) {
        return (
            <EmptyStateCard
                title="You don't have any groups yet"
                description="Groups allow you to manage access to projects and runners."
                data-testid="groups-empty-state"
            >
                {canCreateGroup && (
                    <Button variant="primary" size="sm" data-tracking-id="new-group" onClick={onCreateGroup}>
                        New Group
                    </Button>
                )}
            </EmptyStateCard>
        );
    }

    return <GroupsList groups={groups} />;
};
